import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Star, Zap, ArrowRight } from 'lucide-react';
import { Brain, Sparkles, Database, Target, LineChart } from 'lucide-react';

interface ResearchModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (mode: any) => void;
  selectedMode: any;
  isDarkMode: boolean;
  style?: React.CSSProperties;
}

const researchModeDetails = [
  {
    id: 'default',
    name: 'Standard Research',
    icon: Brain,
    description: 'Balanced research approach for general market inquiries',
    longDescription: `The Standard Research mode provides a balanced analytical approach, combining fundamental and technical factors. It's ideal for getting comprehensive market insights and is suitable for both beginners and experienced investors.`,
    bestFor: [
      'General market research',
      'Company overviews',
      'Basic investment decisions',
      'Understanding market trends'
    ],
    features: [
      'Balanced analysis of multiple factors',
      'Clear, straightforward insights',
      'Comprehensive market context',
      'Easy-to-understand explanations'
    ],
    color: 'blue',
  },
  {
    id: 'discovery',
    name: 'Discovery Mode',
    icon: Sparkles,
    description: 'Explore new market opportunities and emerging trends',
    longDescription: 'Discovery Mode helps you uncover hidden opportunities and emerging trends in the market. It focuses on identifying potential growth areas and innovative companies that might be flying under the radar.',
    bestFor: [
      'Finding new investment opportunities',
      'Exploring emerging sectors',
      'Identifying market trends early',
      'Understanding innovative companies'
    ],
    features: [
      'Trend analysis and prediction',
      'Innovation tracking',
      'Growth potential assessment',
      'Alternative data analysis'
    ],
    color: 'purple',
  },
  {
    id: 'deep-dive',
    name: 'Deep Dive Analysis',
    icon: Database,
    description: 'Thorough analysis with comprehensive data examination',
    longDescription: 'Deep Dive mode provides exhaustive analysis using comprehensive data sets. It\'s designed for thorough research that considers multiple angles and data points for more informed decision-making.',
    bestFor: [
      'Detailed company analysis',
      'Complex market situations',
      'Long-term investment research',
      'Risk assessment'
    ],
    features: [
      'Comprehensive data analysis',
      'Multiple perspective evaluation',
      'Historical trend analysis',
      'Detailed risk assessment'
    ],
    color: 'green',
  },
  {
    id: 'contrarian',
    name: 'Contrarian Analysis',
    icon: Target,
    description: 'Challenge market consensus and explore alternative views',
    longDescription: 'Contrarian Analysis mode helps you think differently about market consensus. It identifies potential flaws in common assumptions and explores alternative viewpoints for better-informed decisions.',
    bestFor: [
      'Challenging popular opinions',
      'Finding overlooked opportunities',
      'Risk mitigation',
      'Understanding market biases'
    ],
    features: [
      'Consensus analysis',
      'Alternative viewpoint exploration',
      'Bias identification',
      'Counter-trend opportunities'
    ],
    color: 'orange',
  },
  {
    id: 'technical',
    name: 'Technical Analysis',
    icon: LineChart,
    description: 'Focus on technical indicators and chart patterns',
    longDescription: `Technical Analysis mode concentrates on price movements, patterns, and technical indicators. It's perfect for traders and investors who rely on chart analysis and technical signals.`,
    bestFor: [
      'Chart pattern analysis',
      'Trading decisions',
      'Market timing',
      'Trend identification'
    ],
    features: [
      'Pattern recognition',
      'Technical indicator analysis',
      'Price movement study',
      'Volume analysis'
    ],
    color: 'cyan',
  }
];

export const ResearchModeModal: React.FC<ResearchModeModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedMode,
  isDarkMode
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (isMobile) {
        window.scrollTo(0, 0);
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  const getColorClasses = (mode: typeof researchModeDetails[0], isSelected: boolean) => {
    const colorMap = {
      blue: {
        selected: isDarkMode 
          ? 'bg-blue-900/20 border-blue-500/30' 
          : 'bg-blue-50 border-blue-300',
        icon: isDarkMode 
          ? 'bg-blue-800 text-blue-200' 
          : 'bg-blue-100 text-blue-600',
        dot: 'bg-blue-500',
        text: isDarkMode ? 'text-blue-300' : 'text-blue-700'
      },
      purple: {
        selected: isDarkMode 
          ? 'bg-purple-900/20 border-purple-500/30' 
          : 'bg-purple-50 border-purple-300',
        icon: isDarkMode 
          ? 'bg-purple-800 text-purple-200' 
          : 'bg-purple-100 text-purple-600',
        dot: 'bg-purple-500',
        text: isDarkMode ? 'text-purple-300' : 'text-purple-700'
      },
      green: {
        selected: isDarkMode 
          ? 'bg-green-900/20 border-green-500/30' 
          : 'bg-green-50 border-green-300',
        icon: isDarkMode 
          ? 'bg-green-800 text-green-200' 
          : 'bg-green-100 text-green-600',
        dot: 'bg-green-500',
        text: isDarkMode ? 'text-green-300' : 'text-green-700'
      },
      orange: {
        selected: isDarkMode 
          ? 'bg-orange-900/20 border-orange-500/30' 
          : 'bg-orange-50 border-orange-300',
        icon: isDarkMode 
          ? 'bg-orange-800 text-orange-200' 
          : 'bg-orange-100 text-orange-600',
        dot: 'bg-orange-500',
        text: isDarkMode ? 'text-orange-300' : 'text-orange-700'
      },
      cyan: {
        selected: isDarkMode 
          ? 'bg-cyan-900/20 border-cyan-500/30' 
          : 'bg-cyan-50 border-cyan-300',
        icon: isDarkMode 
          ? 'bg-cyan-800 text-cyan-200' 
          : 'bg-cyan-100 text-cyan-600',
        dot: 'bg-cyan-500',
        text: isDarkMode ? 'text-cyan-300' : 'text-cyan-700'
      }
    };

    const color = mode.color || 'blue';
    return isSelected ? colorMap[color as keyof typeof colorMap] : {
      selected: isDarkMode 
        ? 'border-gray-700 bg-gray-800/40 hover:border-gray-600' 
        : 'border-gray-200 bg-white hover:border-gray-300',
      icon: isDarkMode
        ? 'bg-gray-700 text-gray-300'
        : 'bg-gray-100 text-gray-600',
      dot: isDarkMode ? 'bg-gray-500' : 'bg-gray-400',
      text: isDarkMode ? 'text-gray-400' : 'text-gray-500'
    };
  };

  const MobileModal = () => (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto"
      style={{ 
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
      }}
    >
      <div className={`rounded-t-xl shadow-lg overflow-hidden ${
        isDarkMode 
          ? 'bg-gray-900 border-t border-x border-gray-700' 
          : 'bg-white border-t border-x border-gray-200'
      }`}>
        <div className="flex justify-center py-2">
          <div className="w-12 h-1.5 rounded-full bg-gray-500/30"></div>
        </div>
        
        <div className={`px-4 py-3 border-b ${
          isDarkMode ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg mr-3 ${
                isDarkMode 
                  ? 'bg-blue-900/30 text-blue-300' 
                  : 'bg-blue-100 text-blue-600'
              }`}>
                <Brain className="w-5 h-5" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Research Mode
                </h2>
                <p className={`text-xs ${
                  isDarkMode ? 'text-blue-300' : 'text-blue-700'
                }`}>
                  <span className="inline-flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    Choose how Prism AI approaches your research
                  </span>
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto">
          <div className="space-y-3">
            {researchModeDetails.map((mode) => {
              const isSelected = selectedMode.id === mode.id;
              const colorClasses = getColorClasses(mode, isSelected);
              
              return (
                <button
                  key={mode.id}
                  onClick={() => {
                    onSelect(mode);
                    onClose();
                  }}
                  className={`relative p-4 rounded-lg border transition-colors w-full text-left flex items-start ${
                    isSelected
                      ? `${colorClasses.selected} shadow-md`
                      : colorClasses.selected
                  }`}
                >
                  <div className={`p-2 rounded-lg ${colorClasses.icon} flex-shrink-0`}>
                    <mode.icon size={18} />
                  </div>
                  
                  <div className="ml-3 flex-1">
                    <h3 className={`font-medium ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {mode.name}
                    </h3>
                    <p className={`text-xs mt-1 ${
                      isSelected
                        ? colorClasses.text
                        : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {mode.description}
                    </p>
                    
                    <div className="mt-2 flex flex-wrap gap-2">
                      {mode.bestFor.slice(0, 2).map((item, idx) => (
                        <span 
                          key={idx}
                          className={`inline-flex items-center text-xs px-2 py-1 rounded-full ${
                            isSelected
                              ? isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                              : isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="ml-2 flex-shrink-0">
                      <CheckCircle2 className={`w-5 h-5 ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-500'
                      }`} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className={`p-4 ${
          isDarkMode 
            ? 'bg-gray-800 border-t border-gray-700' 
            : 'bg-gray-50 border-t border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`flex items-center justify-center w-full px-4 py-3 rounded-lg ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            } transition-colors font-medium`}
          >
            <span>Continue with {selectedMode.name}</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const DesktopModal = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
      className="relative w-full max-w-4xl mx-auto my-8 p-1.5 z-10"
    >
      <div className={`relative w-full rounded-xl shadow-lg overflow-hidden ${
        isDarkMode 
          ? 'bg-gray-900 border border-gray-700' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className={`p-4 border-b ${
          isDarkMode ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg mr-3 ${
                isDarkMode 
                  ? 'bg-blue-900/30 text-blue-300' 
                  : 'bg-blue-100 text-blue-600'
              }`}>
                <Brain className="w-5 h-5" />
              </div>
              <div>
                <h2 className={`text-xl font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Research Mode
                </h2>
                <p className={`text-sm ${
                  isDarkMode ? 'text-blue-300' : 'text-blue-700'
                }`}>
                  <span className="inline-flex items-center">
                    <Zap className="w-3.5 h-3.5 mr-1.5" />
                    Customize how Prism AI approaches your market research
                  </span>
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {researchModeDetails.map((mode) => {
              const isSelected = selectedMode.id === mode.id;
              const colorClasses = getColorClasses(mode, isSelected);
              
              return (
                <button
                  key={mode.id}
                  onClick={() => {
                    onSelect(mode);
                    onClose();
                  }}
                  className={`relative p-4 rounded-lg border transition-colors h-full text-left ${
                    isSelected
                      ? `${colorClasses.selected} shadow-md`
                      : colorClasses.selected
                  }`}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-start mb-3">
                      <div className={`p-2 rounded-lg ${colorClasses.icon}`}>
                        <mode.icon size={18} />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className={`font-medium ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {mode.name}
                        </h3>
                        <p className={`text-xs mt-1 line-clamp-2 ${
                          isSelected
                            ? colorClasses.text
                            : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {mode.description}
                        </p>
                      </div>
                      
                      {isSelected && (
                        <div className="ml-2">
                          <CheckCircle2 className={`w-4 h-4 ${
                            isDarkMode ? 'text-blue-400' : 'text-blue-500'
                          }`} />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 text-left mt-auto">
                      <div>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 flex items-center ${colorClasses.text}`}>
                          <Star className="w-3 h-3 mr-1" />
                          Best For
                        </h4>
                        <ul className="space-y-1 pl-1">
                          {mode.bestFor.slice(0, 3).map((item, idx) => (
                            <li 
                              key={idx} 
                              className={`flex items-center text-xs ${
                                isSelected
                                  ? isDarkMode ? 'text-white' : 'text-gray-900'
                                  : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full mr-2 ${colorClasses.dot}`} />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`p-4 ${
          isDarkMode 
            ? 'bg-gray-800 border-t border-gray-700' 
            : 'bg-gray-50 border-t border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`p-1.5 rounded-lg ${
              isDarkMode 
                ? 'bg-blue-900/30 text-blue-300' 
                : 'bg-blue-100 text-blue-600'
            } mr-3`}>
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <p className={`text-xs ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Change research modes anytime to adjust how Prism AI analyzes the market.
            </p>
          </div>
          
          <div className="mt-3 flex justify-end">
            <button
              onClick={onClose}
              className={`flex items-center px-4 py-2 rounded-lg ${
                isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } transition-colors text-sm`}
            >
              <span>Continue</span>
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className={`fixed font-sans inset-0 z-[9999] flex items-center justify-center ${
          isMobile ? 'items-end' : 'p-4'
        } ${isDarkMode ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/50'}`}>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0" 
            onClick={onClose}
          />

          {isMobile ? <MobileModal /> : <DesktopModal />}
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};